import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Calendar as CalIcon, ChevronLeft, ChevronRight, Loader2,
  X, AlertTriangle, RefreshCw, Trash2, Save,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authFetch = (path, opts = {}) => {
  const token = localStorage.getItem('rbr_token');
  return fetch(`${API_URL}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });
};

const iso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const startOfMonthGrid = (year, month /* 0-11 */) => {
  const first = new Date(year, month, 1);
  const startDow = first.getDay();
  return new Date(year, month, 1 - startDow);
};

const rangeDates = (start, count) => {
  const out = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    out.push(d);
  }
  return out;
};

const SLOT_COLORS = {
  daily_reflection:      'bg-emerald-50 text-emerald-800 border-emerald-200',
  daily_affirmation:     'bg-amber-50   text-amber-800   border-amber-200',
  featured_rowdy_moment: 'bg-rose-50    text-rose-800    border-rose-200',
  featured_meditation:   'bg-sky-50     text-sky-800     border-sky-200',
  featured_video:        'bg-violet-50  text-violet-800  border-violet-200',
};

// -------------------- Content picker (searches admin/content) --------------------

const ContentPicker = ({ acceptedTypes, onPick, currentContentId }) => {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = useCallback(async (query) => {
    setLoading(true);
    try {
      // Pull the first ACCEPTED type; server accepts one type filter per request.
      // For multi-type slots (featured_meditation), broaden by searching without
      // a type filter and client-side filtering.
      const params = new URLSearchParams();
      params.set('status', 'published');
      params.set('pageSize', '25');
      if (query) params.set('q', query);
      if (acceptedTypes.length === 1) params.set('type', acceptedTypes[0]);
      const r = await authFetch(`/api/admin/content?${params.toString()}`);
      const d = await r.json();
      let out = d.items || [];
      if (acceptedTypes.length > 1) {
        out = out.filter((c) => acceptedTypes.includes(c.type));
      }
      setItems(out);
    } finally { setLoading(false); }
  }, [acceptedTypes]);

  useEffect(() => { search(''); }, [search]);

  return (
    <div>
      <div className="flex gap-2 mb-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') search(q); }}
          placeholder={`Search ${acceptedTypes.join(' / ')}…`}
          data-testid="calendar-picker-search"
          className="flex-1 px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm focus:outline-none focus:border-[#C79A4A]"
        />
        <button onClick={() => search(q)} className="px-3 py-2 rounded-lg text-sm border border-stone-200 hover:bg-white">Go</button>
      </div>
      <div className="max-h-72 overflow-y-auto divide-y divide-stone-100 border border-stone-200 rounded-lg bg-white">
        {loading ? (
          <p className="text-center text-stone-500 text-[12px] py-6"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Searching…</p>
        ) : items.length === 0 ? (
          <p className="text-center text-stone-500 text-[12px] py-6">No results.</p>
        ) : (
          items.map((c) => (
            <button
              key={c.id}
              onClick={() => onPick(c)}
              data-testid={`calendar-picker-pick-${c.id}`}
              className={`w-full text-left flex items-center gap-3 px-3 py-2 hover:bg-[#F6F1E7] ${currentContentId === c.id ? 'bg-[#F6F1E7]' : ''}`}
            >
              <div className="w-8 h-8 rounded bg-[#EFE7D4] flex-shrink-0 overflow-hidden">
                {c.coverImageUrl && <img src={c.coverImageUrl} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-semibold text-stone-800 truncate">{c.title}</p>
                <p className="text-[10px] uppercase tracking-wider text-[#C79A4A]">{c.type}</p>
              </div>
              {currentContentId === c.id && <span className="text-[10px] text-emerald-700 font-semibold">Selected</span>}
            </button>
          ))
        )}
      </div>
    </div>
  );
};

// -------------------- Assignment modal --------------------

const AssignmentModal = ({ open, onClose, date, slots, initial, onSaved }) => {
  const [slotKey, setSlotKey] = useState(initial?.slot || 'daily_reflection');
  const [pickedContent, setPickedContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [confirmingClear, setConfirmingClear] = useState(false);

  useEffect(() => {
    if (open) {
      setSlotKey(initial?.slot || 'daily_reflection');
      setPickedContent(initial?.content || null);
      setConflict(null);
      setConfirmingClear(false);
    }
  }, [open, initial]);

  const currentSlot = slots.find((s) => s.key === slotKey);

  useEffect(() => {
    // Live conflict check when a pick + slot is set
    if (!pickedContent || !date || !slotKey) { setConflict(null); return; }
    const params = new URLSearchParams({
      contentId: pickedContent.id, date, slot: slotKey, timezone: 'UTC',
    });
    authFetch(`/api/admin/calendar/conflicts?${params.toString()}`)
      .then((r) => r.json())
      .then((d) => setConflict(d && d.hasConflict ? d : null))
      .catch(() => setConflict(null));
  }, [pickedContent, date, slotKey]);

  if (!open) return null;

  const save = async () => {
    if (!pickedContent) return toast.error('Pick a content item');
    setSaving(true);
    try {
      const r = await authFetch('/api/admin/calendar', {
        method: 'POST',
        body: JSON.stringify({
          date, slot: slotKey, contentId: pickedContent.id, timezone: 'UTC',
        }),
      });
      const data = await r.json();
      if (!r.ok) { toast.error(data.detail || 'Save failed'); return; }
      toast.success('Assigned');
      onSaved();
    } finally { setSaving(false); }
  };

  const clearAssignment = async () => {
    if (!initial?.id) return;
    if (!confirmingClear) { setConfirmingClear(true); return; }
    setSaving(true);
    try {
      const r = await authFetch(`/api/admin/calendar/${initial.id}`, { method: 'DELETE' });
      if (r.ok) { toast.success('Cleared'); onSaved(); }
      else { const d = await r.json(); toast.error(d.detail || 'Failed'); }
    } finally { setSaving(false); setConfirmingClear(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4" data-testid="calendar-assign-modal" onClick={onClose}>
      <div className="bg-[#FFFCF6] w-full max-w-lg rounded-2xl border border-stone-200 shadow-lg p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-xl text-stone-800" style={{ fontFamily: "'DM Serif Display', serif" }}>Assign for {date}</h3>
            <p className="text-[12px] text-stone-500">Manual assignment overrides the auto pick for this date and slot.</p>
          </div>
          <button onClick={onClose} className="p-1 text-stone-400 hover:text-stone-700" data-testid="calendar-modal-close"><X className="w-4 h-4" /></button>
        </div>

        <label className="text-[11px] font-semibold uppercase tracking-wider text-stone-500 block mb-1.5">Slot</label>
        <select value={slotKey} onChange={(e) => { setSlotKey(e.target.value); setPickedContent(null); }} className="w-full px-3 py-2.5 rounded-xl bg-white border border-stone-200 text-sm mb-3" data-testid="calendar-modal-slot">
          {slots.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>

        <label className="text-[11px] font-semibold uppercase tracking-wider text-stone-500 block mb-1.5">
          Content ({currentSlot?.acceptedTypes.join(' / ')})
        </label>
        <ContentPicker
          acceptedTypes={currentSlot?.acceptedTypes || []}
          onPick={setPickedContent}
          currentContentId={pickedContent?.id}
        />

        {pickedContent && (
          <div className="mt-3 p-2.5 bg-[#F6F1E7] rounded-lg flex items-center gap-2">
            <div className="w-8 h-8 rounded bg-white overflow-hidden">
              {pickedContent.coverImageUrl && <img src={pickedContent.coverImageUrl} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-semibold text-stone-800 truncate" data-testid="calendar-modal-picked-title">{pickedContent.title}</p>
              <p className="text-[10px] uppercase tracking-wider text-[#C79A4A]">{pickedContent.type}</p>
            </div>
          </div>
        )}

        {conflict && (
          <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-[12px] text-amber-900" data-testid="calendar-modal-conflict">
            <div className="flex items-center gap-1.5 font-semibold mb-1"><AlertTriangle className="w-4 h-4" /> Heads up</div>
            {conflict.slotConflict && (
              <p>A different item is already assigned to this slot on this date. Saving will replace it.</p>
            )}
            {(conflict.repeatUsage || []).length > 0 && (
              <p className="mt-1">This same content is also assigned on: {conflict.repeatUsage.map((r) => r.date).join(', ')}. Consider spacing it out.</p>
            )}
          </div>
        )}

        <div className="flex gap-2 mt-4 justify-end">
          {initial?.id && (
            <button onClick={clearAssignment} disabled={saving} data-testid="calendar-modal-clear" className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm disabled:opacity-60 ${confirmingClear ? 'bg-rose-600 text-white hover:bg-rose-700' : 'border border-rose-200 text-rose-600 hover:bg-rose-50'}`}>
              <Trash2 className="w-4 h-4" /> {confirmingClear ? 'Tap again to confirm' : 'Clear'}
            </button>
          )}
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm text-stone-600 hover:bg-white">Cancel</button>
          <button onClick={save} disabled={saving || !pickedContent} data-testid="calendar-modal-save" className="flex items-center gap-2 px-5 py-2 rounded-xl bg-[#173F35] text-[#FFFCF6] text-sm font-semibold hover:bg-[#0D2B24] disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

// -------------------- Root Calendar tab --------------------

const CalendarTab = () => {
  const [view, setView] = useState('month');   // month | week | day
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [slots, setSlots] = useState([]);
  const [assignments, setAssignments] = useState([]);   // list from API
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDate, setModalDate] = useState(null);
  const [modalInitial, setModalInitial] = useState(null);

  const range = useMemo(() => {
    if (view === 'month') {
      const start = startOfMonthGrid(cursor.getFullYear(), cursor.getMonth());
      return { start, count: 42 };  // 6-week grid
    }
    if (view === 'week') {
      const start = new Date(cursor);
      start.setDate(cursor.getDate() - cursor.getDay());
      return { start, count: 7 };
    }
    return { start: new Date(cursor), count: 1 };
  }, [view, cursor]);

  const days = useMemo(() => rangeDates(range.start, range.count), [range]);

  const loadSlots = useCallback(async () => {
    try {
      const r = await authFetch('/api/admin/calendar/slots');
      if (r.ok) setSlots((await r.json()).slots || []);
    } catch { /* ignore */ }
  }, []);

  const loadAssignments = useCallback(async () => {
    if (days.length === 0) return;
    setLoading(true);
    try {
      const from = iso(days[0]);
      const to = iso(days[days.length - 1]);
      const r = await authFetch(`/api/admin/calendar?from=${from}&to=${to}&timezone=UTC`);
      if (r.ok) setAssignments((await r.json()).items || []);
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { loadSlots(); }, [loadSlots]);
  useEffect(() => { loadAssignments(); }, [loadAssignments]);

  const cellFor = (dateIso) => assignments.filter((a) => a.date === dateIso);

  const openAssign = (d, initial = null) => {
    setModalDate(iso(d));
    setModalInitial(initial);
    setModalOpen(true);
  };

  const nav = (delta) => {
    const d = new Date(cursor);
    if (view === 'month') d.setMonth(cursor.getMonth() + delta);
    else if (view === 'week') d.setDate(cursor.getDate() + delta * 7);
    else d.setDate(cursor.getDate() + delta);
    setCursor(d);
  };

  const goToday = () => setCursor(new Date());

  const titleFor = () => {
    if (view === 'month') return cursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    if (view === 'week') {
      const end = new Date(range.start); end.setDate(range.start.getDate() + 6);
      return `${range.start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} — ${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    return cursor.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  const today = iso(new Date());

  return (
    <div data-testid="admin-calendar-tab" className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 rounded-lg border border-stone-200 bg-white overflow-hidden">
          <button onClick={() => nav(-1)} data-testid="calendar-nav-prev" className="px-2.5 py-1.5 hover:bg-[#F6F1E7]"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={goToday} data-testid="calendar-nav-today" className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider hover:bg-[#F6F1E7]">Today</button>
          <button onClick={() => nav(1)} data-testid="calendar-nav-next" className="px-2.5 py-1.5 hover:bg-[#F6F1E7]"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <h4 className="text-lg text-stone-800" style={{ fontFamily: "'DM Serif Display', serif" }}>{titleFor()}</h4>
        <div className="ml-auto flex gap-1 rounded-lg border border-stone-200 bg-white overflow-hidden text-[11px] font-semibold uppercase tracking-wider">
          {['month', 'week', 'day'].map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              data-testid={`calendar-view-${v}`}
              className={`px-3 py-1.5 ${view === v ? 'bg-[#173F35] text-[#FFFCF6]' : 'text-stone-600 hover:bg-[#F6F1E7]'}`}
            >{v}</button>
          ))}
        </div>
        <button onClick={loadAssignments} data-testid="calendar-refresh" className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] border border-stone-200 hover:bg-white">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* Slot legend */}
      <div className="flex flex-wrap gap-1.5">
        {slots.map((s) => (
          <span key={s.key} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-semibold uppercase tracking-wider ${SLOT_COLORS[s.key] || 'bg-stone-50 text-stone-700 border-stone-200'}`}>
            <CalIcon className="w-3 h-3" /> {s.label}
          </span>
        ))}
      </div>

      {loading && (
        <p className="text-center text-stone-500 py-3 text-[12px]"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</p>
      )}

      {/* Grid */}
      <div className={`grid gap-1 ${view === 'month' ? 'grid-cols-7' : view === 'week' ? 'grid-cols-7' : 'grid-cols-1'}`}>
        {(view === 'month' || view === 'week') && ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d} className="text-[10px] font-semibold uppercase tracking-wider text-stone-400 text-center py-1">{d}</div>
        ))}
        {days.map((d) => {
          const dIso = iso(d);
          const cell = cellFor(dIso);
          const inMonth = view !== 'month' || d.getMonth() === cursor.getMonth();
          const isToday = dIso === today;
          return (
            <div
              key={dIso}
              data-testid={`calendar-cell-${dIso}`}
              className={`min-h-[92px] rounded-lg border p-1.5 cursor-pointer transition-colors ${
                isToday ? 'border-[#C79A4A] bg-[#FFFCF6]' : inMonth ? 'border-stone-200 bg-white hover:bg-[#F6F1E7]' : 'border-stone-100 bg-stone-50 opacity-60 hover:opacity-100'
              }`}
              onClick={() => openAssign(d, null)}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-[11px] font-semibold ${isToday ? 'text-[#C79A4A]' : 'text-stone-600'}`}>{d.getDate()}</span>
                {cell.length > 0 && <span className="text-[9px] text-stone-400">{cell.length}</span>}
              </div>
              <div className="space-y-0.5">
                {cell.slice(0, 4).map((a) => {
                  const slotLabel = slots.find((s) => s.key === a.slot)?.label || a.slot;
                  const isFallback = a.effective === 'auto_fallback';
                  return (
                    <button
                      key={a.id}
                      onClick={(e) => { e.stopPropagation(); openAssign(d, a); }}
                      data-testid={`calendar-item-${a.id}`}
                      className={`w-full text-left px-1.5 py-0.5 rounded text-[9.5px] font-semibold uppercase tracking-wider border ${SLOT_COLORS[a.slot] || 'bg-stone-50 border-stone-200 text-stone-700'} ${isFallback ? 'opacity-60 line-through' : ''}`}
                      title={`${slotLabel} · ${a.content?.title || 'Missing content'}`}
                    >
                      <span className="truncate block normal-case tracking-normal">{a.content?.title || '⚠ unavailable'}</span>
                    </button>
                  );
                })}
                {cell.length > 4 && <p className="text-[9px] text-stone-400 px-1">+{cell.length - 4} more</p>}
              </div>
            </div>
          );
        })}
      </div>

      <AssignmentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        date={modalDate}
        slots={slots}
        initial={modalInitial}
        onSaved={() => { setModalOpen(false); loadAssignments(); }}
      />
    </div>
  );
};

export default CalendarTab;
